import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Row } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import { getProductSearch, getProductSearchByImageHash } from "../auth/apiProduct";
import {
    readHistorySearchLS,
    writeHistorySearchLS,
    clearHistorySearchLS,
    mergeUniqueIds,
    getFirstIds,
    creatHistorySearch,
} from "../auth";
import ButtonPagination from "../pageInside/ButtonPagination";
import Card from '../pageInside/Card';
import elakayEmptyBox from "../Layout/icon/elakayEmptyBox.png";
import { CARD_MODE_STORAGE_KEY, CARD_MODE_EVENT, getCardColumns } from "../../utils/cardDisplaySettings";
import { getProductGridLimit, resolvePaginationLimit } from "../../utils/paginationSettings";
import { buildProductPagination } from "../../utils/productPagination";
import { resolveProductMeta, resolveProductRows } from "../../utils/productResponse";
import InfiniteScrollLoader from "./InfiniteScrollLoader";
import { mergeUniqueById, resolveInfiniteScroll } from "../../utils/infiniteScrollSettings";
import { ProductGridSkeleton } from "./ProductGridPage";

import './ProductGridPage.css';

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["productGridSearch"]?.[key] || fallback;

// ============================================
// HOOK — useQuery (lecture params URL)
// ============================================
function useQuery() {
    return new URLSearchParams(useLocation().search);
}

// ============================================
// HELPER
// ============================================
const getRandomColor = () => {
    const colors = [
        'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
        'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
        'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
        'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
};

// ============================================
// SAVE VIEWED HISTORY — historySearch LS / MongoDB
// ============================================
const saveViewedHistory = async ({
    token,
    newIds,
    limit = 10,
    lsKey = 'historySearch',
    clearLsAfterSync = true,
}) => {
    const oldLs  = readHistorySearchLS(lsKey);
    const merged = mergeUniqueIds(newIds, oldLs, limit);

    if (!token) {
        writeHistorySearchLS(merged, lsKey);
        return { success: true, saved: 'localStorage', ids: merged };
    }

    const res = await creatHistorySearch(token, merged);
    if (res?.success === false) {
        writeHistorySearchLS(merged, lsKey);
        return { success: false, saved: 'localStorage_fallback', ids: merged, api: res };
    }

    if (clearLsAfterSync) clearHistorySearchLS(lsKey);
    return { success: true, saved: 'mongodb', ids: merged, api: res };
};

// ============================================
// RESULT PAGINATION COMPONENT
// ============================================
const ResultPagination = ({
    prodCat          = "",
    skip             = 0,
    limit            = 12,
    size             = 0,
    siseProduct      = 0,
    infoSource,
    style            = "minimal",
    customBackground = null,
    icon             = null,
    showIcon         = true,
    showBadge        = true,
    showPageInfo     = true,
    showTitle        = true,
    animated         = false,
    className        = "",
    textColor        = null,
    textSize         = null,
}) => {
    __setElakayInfoSource(infoSource);
    const pagination = buildProductPagination({ skip, limit, size, totalProducts: siseProduct });
    const { start, end, currentPage, totalPages, totalProducts: realTotal } = pagination;
    const hasData = pagination.size > 0;

    const ofText      = infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.of      || "of";
    const itemsText   = infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.items   || "items";
    const resultsText = infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.results || "results";

    const styleClass  = customBackground ? 'style-custom' : `style-${style}`;
    const customStyle = {
        ...(customBackground ? { background: customBackground } : {}),
        ...(textColor ? { color: textColor } : {}),
        ...(textSize ? { fontSize: textSize } : {}),
    };

    return (
        <div
            className={`pgp-result-bar ${styleClass} ${animated ? 'animated' : ''} ${className}`.trim()}
            style={customStyle}
        >
            <div className="pgp-result-left">
                {showIcon && (
                    <div className="pgp-result-icon">
                        {icon || <FaSearch />}
                    </div>
                )}
                <div className="pgp-result-content">
                    {showTitle ? <h5>
                        {prodCat || resultsText}
                        {showBadge && hasData && (
                            <span className="pgp-result-badge">
                                <HiSparkles size={14} />
                                {realTotal} {itemsText}
                            </span>
                        )}
                    </h5> : null}
                    <p>
                        {hasData
                            ? `${start} - ${end} ${ofText} ${realTotal} ${itemsText}`
                            : `0 ${itemsText}`
                        }
                    </p>
                </div>
            </div>

            {showPageInfo && (
                <div className="pgp-result-right">
                    <div className="pgp-page-info">{__elakayText("t002", "Page")}{currentPage} / {totalPages}
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================
// PRODUCT GRID COMPONENT
// ============================================
const ProductGrid = ({
    productsByArrival = [],
    Card,
    laguangeElakay,
    infoSource,
    promoMaker,
    btnShopPromo,
    refShopName,
    elakayEmptyBox,
    style              = "minimal",
    customBackground   = null,
    columns            = { xs: 2, sm: 2, md: 3, lg: 4 },
    emptyCount         = 12,
    animated           = false,
    animateCards       = false,
    className          = "",
    emptySearchMessage = null,
    search             = "",
    hasActiveQuery     = false,
    textColor          = null,
    textSize           = null,
    cardMode           = 'portrait',
}) => {
    const styleClass    = customBackground ? 'style-custom' : `style-${style}`;
    const animatedClass = animated ? 'animated' : '';
    const customStyle   = {
        ...(customBackground ? { background: customBackground } : {}),
        ...(textColor ? { color: textColor } : {}),
        ...(textSize ? { fontSize: textSize } : {}),
    };

    const renderCards = () => {
        const cards = [];

        productsByArrival?.forEach((product, i) => {
            const similarityPercent = product?._similarityPercent;
            cards.push(
                <div
                    className={`col pgp-product-col ${animateCards ? 'animate-appear' : ''}`}
                    key={product._id || i}
                    style={similarityPercent != null ? { position: 'relative' } : undefined}
                >
                    {similarityPercent != null && (
                        <span className="pgp-similarity-badge">{similarityPercent}%</span>
                    )}
                    <Card
                        laguangeElakay={laguangeElakay}
                        infoSource={infoSource}
                        product={product}
                        classCard0="pgp-card-shell"
                        promoMaker={promoMaker}
                        btnShopPromo={btnShopPromo}
                        refShopName={refShopName}
                        displayMode={cardMode}
                        isGridPage={true}
                    />
                </div>
            );
        });

        if (productsByArrival?.length === 0) {
            for (let f = 0; f < emptyCount; f++) {
                cards.push(
                    <div className="col pgp-product-col" key={`filler-${f}`}>
                        <div className="pgp-empty-card">
                            <div
                                className="pgp-empty-card-image"
                                style={{ background: getRandomColor() }}
                            >
                                {elakayEmptyBox && (
                                    <img
                                        src={elakayEmptyBox}
                                        alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).productGridSearch.t001}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                )}
                            </div>
                            <div className="pgp-empty-card-body">
                                <div className="pgp-skeleton title"></div>
                                <div className="pgp-skeleton subtitle"></div>
                                <div className="pgp-skeleton price"></div>
                            </div>
                        </div>
                    </div>
                );
            }
        }

        return cards;
    };

    return (
        <div
            className={`pgp-grid-wrapper ${styleClass} ${animatedClass} ${className}`.trim()}
            style={customStyle}
        >
            {productsByArrival?.length === 0 && (search || hasActiveQuery) && emptySearchMessage && (
                <div className="pgs-no-results-message">
                    {emptySearchMessage}
                </div>
            )}

            <Row
                className="pgp-product-row"
                xs={columns.xs}
                sm={columns.sm}
                md={columns.md}
                lg={columns.lg}
            >
                {renderCards()}
            </Row>
        </div>
    );
};

// ============================================
// MAIN COMPONENT — ProductGridSearch
// ============================================
//
// Lit search + ci depuis l'URL (query params) par défaut:
//   /search?ref=table&ci=123
//     → search = "table"
//     → ci     = "123"
//
// Les props search/ci peuvent override l'URL si fournies (mode controlled).
//
// ┌──────────────────────────────────────────────────────────────────────┐
// │ MODE 1 — AUTONOME (drop-in replacement du ResutlsSearch v1)         │
// │                                                                      │
// │   <ProductGridSearch                                                 │
// │     myFilters={myFilters}                                            │
// │     infoSource={infoSource}                                          │
// │     token={token}                                                    │
// │   />                                                                 │
// │                                                                      │
// │   → Lit search + ci depuis URL via useQuery                          │
// │   → Fetch automatique au mount et quand URL change                   │
// │   → saveViewedHistory automatique                                    │
// └──────────────────────────────────────────────────────────────────────┘
//
// ┌──────────────────────────────────────────────────────────────────────┐
// │ MODE 2 — CONTROLLED (via SECTIONS_LIBRARY.productSearchGrid)        │
// │                                                                      │
// │   <ProductGridSearch                                                 │
// │     controlled={true}                                                │
// │     search="table"        // override URL                            │
// │     ci="123"              // override URL                            │
// │     productsByArrival={pageData.products}                            │
// │     size={pageData.size}                                             │
// │     skip={paginationProps.skip}                                      │
// │     loadMore={paginationProps.loadMore}                              │
// │   />                                                                 │
// └──────────────────────────────────────────────────────────────────────┘

const ProductGridSearch = ({
    // ── Mode toggle ─────────────────────────────────────────────────
    controlled         = false,

    // ── Display props ───────────────────────────────────────────────
    prodCat            = "search",
    laguangeElakay,
    infoSource,
    promoMaker         = false,
    btnShopPromo,
    refShopName,

    // ── Result bar ──────────────────────────────────────────────────
    // Valeurs par défaut lues depuis AnimationSettings
    // (infoSource.allSettings.productGrids.productGridSearch) si non fournies
    // explicitement par l'appelant — voir effective* plus bas.
    resultStyle        = "minimal",
    resultBackground,
    resultIcon         = null,
    showResultIcon     = true,
    showResultBadge    = true,
    showResultPageInfo = true,
    resultAnimated,

    // ── Grid wrapper ────────────────────────────────────────────────
    gridStyle          = "minimal",
    gridBackground,
    gridAnimated,
    animateCards,

    columns,
    containerStyle     = {},
    containerClassName = "mt-3 mb-3",
    emptyCount         = 12,

    // ── Search params (override URL si fournis) ─────────────────────
    search:    searchProp = null,
    ci:        ciProp     = null,
    myFilters          = { filters: {} },
    sortBy             = "createdAt",
    token              = null,
    onSearchData       = null,

    // ── Controlled mode props ───────────────────────────────────────
    productsByArrival: controlledProducts,
    size:              controlledSize,
    siseProduct:       controlledSiseProduct,
    skip:              controlledSkip,
    limit:             controlledLimit = 12,
    loadMore:          controlledLoadMore,
    loadMore0:         controlledLoadMore0,

    // ── Empty state ─────────────────────────────────────────────────
    emptySearchMessage = null,

    // ── Data the parent's useDataLoader already fetched for this exact
    // facet (loaderType SEARCH) — used once, on the very first render, to
    // avoid firing a duplicate getProductSearch call. Subsequent
    // search/ci/filter changes still self-fetch normally (see
    // initialDataConsumedRef below).
    initialData = null,
    page = 'productSearch',
    sectionId = 'productSearch',
}) => {

    // ═══════════════════════════════════════════════════════════════
    // LECTURE URL — useQuery
    // ═══════════════════════════════════════════════════════════════
    //
    // Priorité :
    //   1. Prop explicite (searchProp / ciProp) si fournie → override
    //   2. Query param URL (?ref=... & ci=...) → défaut
    //
    // → En mode standalone : pas de props → lit URL automatiquement
    // → En mode controlled : props viennent du parent ResutlsSearch
    //   qui a déjà lu l'URL, donc searchProp/ciProp sont fournis

    __setElakayInfoSource(infoSource);

    // Card Display Mode (Portrait/Landscape) — Grid page. Préférence globale
    // (localStorage), poussée en direct par SideDrawer1.js via un événement
    // custom (voir utils/cardDisplaySettings.js).
    const [cardMode, setCardMode] = useState(
        () => (typeof window !== 'undefined' && window.localStorage.getItem(CARD_MODE_STORAGE_KEY)) || 'portrait'
    );
    useEffect(() => {
        const updateMode = (e) => {
            setCardMode(e.detail?.mode || window.localStorage.getItem(CARD_MODE_STORAGE_KEY) || 'portrait');
        };
        window.addEventListener(CARD_MODE_EVENT, updateMode);
        return () => window.removeEventListener(CARD_MODE_EVENT, updateMode);
    }, []);

    // Colonnes par appareil — dépendent du mode actif (portrait/landscape),
    // une prop explicite passée par l'appelant reste prioritaire.
    const effectiveColumns = getCardColumns(cardMode, infoSource?.allSettings?.cardDisplaySettings, columns);

    // Apparence configurable depuis AnimationSettings (page "Pages produits &
    // commandes"). Une prop explicite passée par l'appelant reste prioritaire.
    const gridSettings = infoSource?.allSettings?.productGrids?.productGridSearch || {};
    const effectiveResultBackground = resultBackground ?? gridSettings.resultBackground ?? null;
    const effectiveResultAnimated   = resultAnimated   ?? gridSettings.resultAnimated   ?? false;
    const effectiveGridBackground   = gridBackground   ?? gridSettings.gridBackground   ?? null;
    const effectiveGridAnimated     = gridAnimated     ?? gridSettings.gridAnimated     ?? false;
    const effectiveAnimateCards     = animateCards     ?? gridSettings.animateCards     ?? false;

    const query    = useQuery();
    const search   = searchProp ?? query.get("ref") ?? "";
    const ci       = ciProp     ?? query.get("ci")  ?? null;
    const imgQuery = query.get("imgQuery") ?? null;
    const infiniteConfig = resolveInfiniteScroll(infoSource?.allSettings, page, sectionId);

    // ═══════════════════════════════════════════════════════════════
    // STATE LOCAL — utilisé uniquement en mode autonome
    // ═══════════════════════════════════════════════════════════════
    // Page size — admin-configurable via AnimationSettings → General →
    // "Pagination & Limites" (productGrids.productGridSearch.limit).
    const limit = getProductGridLimit(infoSource?.allSettings, 'productGridSearch', 12);
    const [skip,              setSkip]              = useState(0);
    const [size,              setSize]              = useState(0);
    const [siseProduct,       setSiseProduct]       = useState(0);
    const [productsByArrival, setProductsByArrival] = useState([]);
    const [loadError,         setLoadError]         = useState("");
    const [loading,           setLoading]           = useState(false);
    const requestSequenceRef = useRef(0);
    const loadingMoreRef = useRef(false);
    useEffect(() => () => {
        requestSequenceRef.current += 1;
        loadingMoreRef.current = false;
    }, []);

    const divProdGridSearch = useRef(null);

    const scrollToParagraph = (ref) => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // ═══════════════════════════════════════════════════════════════
    // MODE AUTONOME — API calls
    // ═══════════════════════════════════════════════════════════════

    const applySearchResponse = (response, toSkip, explicitProducts, append = false) => {
        const rows = explicitProducts ?? resolveProductRows(response);
        const meta = resolveProductMeta(response, rows, { totalKeys: ['totalProducts'] });
        setProductsByArrival((current) => append ? mergeUniqueById(current, rows) : rows);
        setSize(meta.size);
        setSiseProduct(meta.total);
        setSkip(toSkip);
        setLoadError(meta.total > 0 && rows.length === 0
            ? "La réponse annonce des produits, mais aucune ligne n'est exploitable."
            : "");
        onSearchData?.(response);
        setLoading(false);
        loadingMoreRef.current = false;
    };

    const searchData = async (toSkip = 0, append = false) => {
        if (loadingMoreRef.current && append) return;
        const requestSequence = ++requestSequenceRef.current;
        loadingMoreRef.current = true;
        setLoading(true);
        setLoadError("");
        // ✅ Recherche par similarité d'image (priorité sur le texte si présente dans l'URL)
        if (imgQuery) {
            const minSimilarity = infoSource?.allSettings?.imageSearch?.minSimilarity;
            try {
                const response = await getProductSearchByImageHash(toSkip, limit, imgQuery, minSimilarity);
                if (requestSequence !== requestSequenceRef.current) return;
                if (!response || response.error) {
                    setLoadError(response?.error || "Impossible de charger la recherche par image.");
                    setLoading(false);
                    loadingMoreRef.current = false;
                    return;
                }
                const results = response.results || [];
                applySearchResponse(response, toSkip, results.map((r) => ({
                    ...r.product,
                    _similarityPercent: r.similarityPercent,
                })), append);
            } catch (error) {
                if (requestSequence === requestSequenceRef.current) {
                    setLoadError(error?.message || "Impossible de charger la recherche par image.");
                    setLoading(false);
                    loadingMoreRef.current = false;
                }
            }
            return;
        }

        // ✅ Garde: pas de search → pas d'appel
        if (!search || search.trim() === '') {
            applySearchResponse({ products: [], size: 0, totalProducts: 0 }, 0, [], false);
            return;
        }

        try {
            const response = await getProductSearch(toSkip, limit, sortBy, ci, search, myFilters.filters);
            if (requestSequence !== requestSequenceRef.current) return;
            if (!response || response.error) {
                setLoadError(response?.error || "Impossible de charger les résultats de recherche.");
                setLoading(false);
                loadingMoreRef.current = false;
                return;
            }
            if (response?.categories?.length > 0) {
                const newIds = getFirstIds(response.categories, 5);
                saveViewedHistory({ token, newIds, limit: 10 });
            }
            applySearchResponse(response, toSkip, undefined, append);
        } catch (error) {
            if (requestSequence === requestSequenceRef.current) {
                setLoadError(error?.message || "Impossible de charger les résultats de recherche.");
                setLoading(false);
                loadingMoreRef.current = false;
            }
        }
    };

    // ── Init + re-fetch quand search / ci / filters / imgQuery changent ────────
    const initialDataConsumedRef = useRef(false);
    useEffect(() => {
        if (controlled) return;
        requestSequenceRef.current += 1;
        loadingMoreRef.current = false;
        if (infiniteConfig.enabled) setProductsByArrival([]);
        if (!initialDataConsumedRef.current && !imgQuery && Array.isArray(initialData?.products)) {
            initialDataConsumedRef.current = true;
            setProductsByArrival(initialData.products);
            setSize(initialData.size ?? 0);
            setSiseProduct(initialData.totalProducts ?? initialData.siseProduct ?? 0);
            setSkip(0);
            return;
        }
        initialDataConsumedRef.current = true;
        searchData(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled, search, ci, imgQuery, limit, JSON.stringify(myFilters.filters), infiniteConfig.enabled]);

    // ═══════════════════════════════════════════════════════════════
    // PAGINATION HANDLERS
    // ═══════════════════════════════════════════════════════════════

    const handleLoadMore = () => {
        if (controlled) {
            controlledLoadMore?.();
            return;
        }
        const toSkip = infiniteConfig.enabled ? productsByArrival.length : skip + limit;
        if (!infiniteConfig.enabled) scrollToParagraph(divProdGridSearch);
        searchData(toSkip, infiniteConfig.enabled);
    };

    const handleLoadMore0 = () => {
        if (controlled) {
            controlledLoadMore0?.();
            return;
        }
        const toSkip = Math.max(0, skip - limit);
        scrollToParagraph(divProdGridSearch);
        searchData(toSkip);
    };

    // ═══════════════════════════════════════════════════════════════
    // RESOLUTION — valeurs finales selon mode
    // ═══════════════════════════════════════════════════════════════
    const finalProducts    = controlled ? (controlledProducts    || []) : productsByArrival;
    const finalSize        = controlled ? (controlledSize        ?? 0)  : size;
    const finalSiseProduct = controlled ? (controlledSiseProduct ?? 0)  : siseProduct;
    const finalSkip        = controlled ? (controlledSkip        ?? 0)  : skip;
    const finalLimit       = controlled
        ? resolvePaginationLimit(controlledLimit, 12)
        : limit;
    const isImageSearch    = !controlled && Boolean(imgQuery);
    const finalProdCat     = isImageSearch
        ? (__elakayText("t003", "Similar images") || prodCat)
        : prodCat;
    const configuredProdCat = infiniteConfig.titleMode === 'custom' ? infiniteConfig.customTitle : finalProdCat;
    const finalEmptyMessage = isImageSearch
        ? (__elakayText("t004", "Aucun produit similaire trouvé") || emptySearchMessage)
        : emptySearchMessage;

    // ═══════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════

    return (
        <div
            ref={divProdGridSearch}
            className={containerClassName}
            style={containerStyle}
        >
            <ResultPagination
                prodCat={configuredProdCat}
                showTitle={infiniteConfig.titleMode !== 'hidden'}
                skip={finalSkip}
                limit={finalLimit}
                size={finalSize}
                siseProduct={finalSiseProduct}
                infoSource={infoSource}
                style={resultStyle}
                customBackground={effectiveResultBackground}
                icon={resultIcon}
                showIcon={showResultIcon}
                showBadge={showResultBadge}
                showPageInfo={showResultPageInfo}
                animated={effectiveResultAnimated}
                textColor={gridSettings.resultTextColor}
                textSize={gridSettings.resultTextSize}
            />

            <ProductGrid
                productsByArrival={finalProducts}
                Card={Card}
                laguangeElakay={laguangeElakay}
                infoSource={infoSource}
                promoMaker={promoMaker}
                btnShopPromo={btnShopPromo}
                refShopName={refShopName}
                elakayEmptyBox={elakayEmptyBox}
                style={gridStyle}
                customBackground={effectiveGridBackground}
                columns={effectiveColumns}
                emptyCount={emptyCount}
                animated={effectiveGridAnimated}
                animateCards={effectiveAnimateCards}
                search={search}
                hasActiveQuery={isImageSearch}
                emptySearchMessage={finalEmptyMessage}
                textColor={gridSettings.gridTextColor}
                textSize={gridSettings.gridTextSize}
                cardMode={cardMode}
            />
            <InfiniteScrollLoader
                enabled={!controlled && infiniteConfig.enabled}
                hasMore={finalProducts.length < finalSiseProduct && finalSize > 0}
                loading={loading && finalProducts.length > 0}
                error={finalProducts.length > 0 ? loadError : ""}
                onLoadMore={handleLoadMore}
                onRetry={handleLoadMore}
                skeleton={<ProductGridSkeleton count={Math.min(finalLimit, 4)} columns={effectiveColumns} />}
            />
            {loadError && (!infiniteConfig.enabled || finalProducts.length === 0) && <p role="alert" className="text-danger">{loadError}</p>}
            {ButtonPagination && !infiniteConfig.enabled && (
                <div>
                    <ButtonPagination
                        infoSource={infoSource}
                        size={finalSize}
                        skip={finalSkip}
                        limit={finalLimit}
                        siseProduct={finalSiseProduct}
                        loadMore={handleLoadMore}
                        loadMore0={handleLoadMore0}
                    />
                </div>
            )}
        </div>
    );
};

export { ResultPagination, ProductGrid, useQuery };
export default ProductGridSearch;
